import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useAuth } from './contexts/AuthContext';
import { isAdmin as checkAdmin, canAccess } from './utils/roleUtils';
import Login from './pages/Login/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { ChatProvider } from './contexts/ChatContext';
import { TabMemoryProvider } from './contexts/TabMemoryContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { KeyboardNavigationProvider } from './contexts/KeyboardNavigationContext';
import { UOMProvider } from './contexts/UOMContext';
import { TaskTypesProvider } from './contexts/TaskTypesContext';
import { MessageProvider, MessageContainer } from './components/Messages';
import './i18n'; // Import i18n configuration
import { useTranslation } from 'react-i18next';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Layout/Header';
import StatusBar from './components/Layout/StatusBar';
import HomePage from './pages/Home/HomePage';
import DepartmentHomePage from './pages/Home/DepartmentHomePage';
import QuotesList from './pages/Quotes/QuotesList';
import ViewQuote from './pages/Quotes/ViewQuote';
import ConvertQuoteToProduction from './pages/Sales/ConvertQuoteToProduction';
import QuoteConversionPage from './pages/Sales/QuoteConversionPage';
import AIChatbot from './pages/Tools/AIChatbot';
import ProductsList from './pages/Products/ProductsList';
import ViewProduct from './pages/Products/ViewProduct';
import ProductionPage from './pages/Production/ProductionPage';
import ProductsPage from './pages/Products/ProductsPage';
import SalesPage from './pages/Sales/SalesPage';
import ProcurementPage from './pages/Procurement/ProcurementPage';
import AdminPage from './pages/Admin/AdminPage';
import MachineSchedules from './pages/Machines/MachineSchedules';
import WarehouseHomePage from './pages/Home/WarehouseHomePage';
import QCHomePage from './pages/Home/QCHomePage';
import OperatorApp from './pages/Operator/OperatorApp';
import './App.css';
import './styles/layout-fix.css';
import './styles/glassmorphism.css';
import './styles/keyboard-navigation.css';

function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, ready } = useTranslation('common');
  const [currentPage, setCurrentPage] = useState('home');
  const [viewQuoteId, setViewQuoteId] = useState(null);
  const [viewProductId, setViewProductId] = useState(null);
  const [convertQuoteId, setConvertQuoteId] = useState(null);
  // Detail target for cross-module navigation (e.g. /sales-orders/123)
  const [initialDetailTarget, setInitialDetailTarget] = useState(null);

  // Update document title with username
  useEffect(() => {
    if (user) {
      document.title = `MPS ERP - ${user.username || user.full_name || 'User'}`;
    } else {
      document.title = 'MPS ERP';
    }
  }, [user]);

  // Sync URL with current page
  const lastLocationRef = useRef(null);
  useEffect(() => {
    // Only the query changed (a module tab in ?tab=/?sub=): nothing to route here, and running again
    // would re-open the detail of /sales-orders/:id, /inventory/items/:id, …
    const last = lastLocationRef.current;
    lastLocationRef.current = location;
    if (last && last.pathname === location.pathname && last.search !== location.search && !location.state) {
      return;
    }

    const path = location.pathname.slice(1) || 'home';
    
    // Handle conversion route
    if (path.startsWith('sales/quotes/') && path.endsWith('/convert')) {
      const quoteId = path.split('/')[2]; // Extract quote ID from sales/quotes/{id}/convert
      setConvertQuoteId(quoteId);
      setCurrentPage('convert-quote');
      return;
    }
    
    // Handle quote detail route
    if (path.startsWith('sales/quotes/') && !path.endsWith('/convert')) {
      const quoteId = path.split('/')[2]; // Extract quote ID from sales/quotes/{id}
      setViewQuoteId(quoteId);
      setCurrentPage('view-quote');
      return;
    }

    // Handle sales order detail route (legacy URL redirect)
    if (path.startsWith('sales/orders/')) {
      const orderId = path.split('/')[2];
      navigate(`/sales-orders/${orderId}`, { replace: true });
      return;
    }

    // Handle /sales-orders/{id} → sales page with order detail
    if (path.match(/^sales-orders\/\d+$/)) {
      const orderId = path.split('/')[1];
      setInitialDetailTarget({ type: 'salesOrder', id: orderId });
      setCurrentPage('sales');
      return;
    }

    // Handle /sales-orders → sales page orders tab
    if (path === 'sales-orders') {
      setInitialDetailTarget({ type: 'salesOrdersList' });
      setCurrentPage('sales');
      return;
    }

    // Handle /production-orders/{id} → production page with order detail (numeric id or PO-number)
    if (path.match(/^production-orders\/.+$/) && path !== 'production-orders/') {
      const orderId = path.split('/').slice(1).join('/');
      setInitialDetailTarget({ type: 'productionOrder', id: orderId, innerTab: location.state?.innerTab || null });
      setCurrentPage('production');
      return;
    }

    // Handle /production-orders → production page orders tab
    if (path === 'production-orders') {
      setInitialDetailTarget({ type: 'productionOrdersList' });
      setCurrentPage('production');
      return;
    }

    // Handle /quotes/{id} → sales page with quote detail
    if (path.match(/^quotes\/\d+$/)) {
      const quoteId = path.split('/')[1];
      setInitialDetailTarget({ type: 'quote', id: quoteId });
      setCurrentPage('sales');
      return;
    }

    // Handle /tasks/{id} → production page tasks tab with task detail
    if (path.match(/^tasks\/\d+$/)) {
      const taskId = path.split('/')[1];
      setInitialDetailTarget({ type: 'task', id: taskId });
      setCurrentPage('production');
      return;
    }

    // Handle /invoices/{id} → sales page invoices tab with the invoice open
    if (path.match(/^invoices\/\d+$/)) {
      setInitialDetailTarget({ type: 'invoice', id: path.split('/')[1] });
      setCurrentPage('sales');
      return;
    }

    // Handle /customers/{id} → sales page customers tab with the customer open
    if (path.match(/^customers\/\d+$/)) {
      setInitialDetailTarget({ type: 'customer', id: path.split('/')[1] });
      setCurrentPage('sales');
      return;
    }

    // Handle /inventory/items/{id} → inventory module with the item detail open
    if (path.match(/^inventory\/items\/\d+$/)) {
      const itemId = path.split('/')[2];
      setInitialDetailTarget({ type: 'inventoryItem', id: itemId });
      setCurrentPage('inventory');
      return;
    }

    // Allow navigation away from any page, but prevent setting the same page twice
    if (path !== currentPage) {
      // Only prevent navigation if we're going to a detail page from the same category
      const preventNavigation = (
        (['view-quote', 'edit-quote', 'new-quote'].includes(currentPage) && path === 'quotes') ||
        (['view-product', 'edit-product', 'new-product'].includes(currentPage) && path === 'products')
      );

      if (!preventNavigation) {
        setCurrentPage(path);
      }
    }
  }, [location]);

  // Clear detail target after child component has consumed it
  const handleDetailConsumed = () => {
    setInitialDetailTarget(null);
  };

  // Handle page changes
  const handlePageChange = (pageId) => {
    setCurrentPage(pageId);
    setViewQuoteId(null); // Reset quote view when changing pages
    setViewProductId(null); // Reset product view when changing pages
    setConvertQuoteId(null); // Reset conversion state when changing pages

    // Update URL to match the page
    if (!['view-quote', 'edit-quote', 'new-quote', 'view-product', 'edit-product', 'new-product'].includes(pageId)) {
      navigate(`/${pageId}`);
    }
  };

  const handleViewQuote = (quoteId) => {
    setViewQuoteId(quoteId);
    setCurrentPage('view-quote');
  };

  const handleEditQuote = (quoteId) => {
    setViewQuoteId(quoteId);
    setCurrentPage('edit-quote');
  };

  const handleConvertQuote = (quoteId) => {
    setConvertQuoteId(quoteId);
    setCurrentPage('convert-quote');
  };

  const handleViewProduct = (productId) => {
    setViewProductId(productId);
    setCurrentPage('view-product');
  };

  const handleEditProduct = (productId) => {
    setViewProductId(productId);
    setCurrentPage('edit-product');
  };

  const handleNewProduct = () => {
    setCurrentPage('new-product');
  };

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input or select
      if (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.tagName === 'SELECT' ||
          e.target.contentEditable === 'true') {
        return;
      }

      // F-key navigation - mapped to match header navigation
      if (e.key === 'F1') {
        e.preventDefault();
        handlePageChange('home');
      } else if (e.key === 'F2') {
        e.preventDefault();
        if (canAccess(user, 'production')) handlePageChange('production');
      } else if (e.key === 'F3') {
        e.preventDefault();
        if (canAccess(user, 'products')) handlePageChange('products');
      } else if (e.key === 'F4') {
        e.preventDefault();
        if (canAccess(user, 'sales')) handlePageChange('sales');
      } else if (e.key === 'F5') {
        e.preventDefault();
        if (canAccess(user, 'procurement')) handlePageChange('procurement');
      } else if (e.key === 'F6') {
        e.preventDefault();
        if (canAccess(user, 'inventory')) handlePageChange('inventory');
      } else if (e.key === 'F7') {
        e.preventDefault();
        if (canAccess(user, 'admin')) handlePageChange('admin');
      } else if (e.key === 'F8') {
        e.preventDefault();
        if (canAccess(user, 'ai')) handlePageChange('ai');
      } else if (e.key === 'F10') {
        e.preventDefault();
        if (canAccess(user, 'qc')) handlePageChange('qc');
      }
      // ESC key for back navigation - only if not focused on grid
      else if (e.key === 'Escape') {
        const focusedElement = document.activeElement;
        const isGridFocused = focusedElement && focusedElement.classList.contains('grid-table-container');

        // If grid is focused, let it handle ESC first (to blur)
        // Otherwise handle back navigation
        if (!isGridFocused) {
          e.preventDefault();
          handleBackNavigation();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, viewQuoteId, viewProductId]);

  // Handle ESC back navigation based on current page
  const handleBackNavigation = () => {
    // From view/edit pages back to list pages
    if (currentPage === 'view-quote' || currentPage === 'edit-quote') {
      handlePageChange('quotes');
    } else if (currentPage === 'new-quote') {
      handlePageChange('quotes');
    } else if (currentPage === 'convert-quote') {
      handlePageChange('sales');
    } else if (currentPage === 'view-product' || currentPage === 'edit-product') {
      handlePageChange('products');
    } else if (currentPage === 'new-product') {
      handlePageChange('products');
    }
    // From main pages - ESC does nothing (already at top level)
    else if (currentPage === 'production' || currentPage === 'customer' || currentPage === 'products' ||
             currentPage === 'sales' || currentPage === 'procurement' || currentPage === 'admin' || currentPage === 'ai') {
      // Do nothing - already at top level
    }
    // Dashboard: ESC does nothing (already at top level)
  };

  const renderPage = () => {
    // Check if we're on a department home page
    if (currentPage.startsWith('home/')) {
      return <DepartmentHomePage />;
    }

    const guard = (pageId, component) => {
      if (!canAccess(user, pageId)) {
        handlePageChange('home');
        return <HomePage />;
      }
      return component;
    };

    switch (currentPage) {
      case 'home':
        return <HomePage />;
      case 'production':
        return guard('production', <ProductionPage
          initialDetailTarget={initialDetailTarget}
          onDetailConsumed={handleDetailConsumed}
        />);
      case 'products':
        return guard('products', <ProductsPage />);
      case 'sales':
        return guard('sales', <SalesPage
          initialDetailTarget={initialDetailTarget}
          onDetailConsumed={handleDetailConsumed}
        />);
      case 'procurement':
        return guard('procurement', <ProcurementPage />);
      case 'admin':
        return guard('admin', <AdminPage />);
      case 'ai':
        return guard('ai', <AIChatbot />);
      case 'qc':
        return guard('qc', <QCHomePage />);
      case 'machine-schedules':
        return <MachineSchedules />;
      case 'convert-quote':
        return convertQuoteId ? (
          <QuoteConversionPage quoteId={convertQuoteId} onBack={() => handlePageChange('quotes')} />
        ) : (
          <div className="page-container">{t('app.quoteIdNotFound')}</div>
        );

      // Legacy routes for existing components (to be migrated)
      case 'quotes':
        return (
          <QuotesList
            onViewQuote={handleViewQuote}
            onEditQuote={handleEditQuote}
            onNewQuote={() => handlePageChange('new-quote')}
          />
        );
      case 'new-quote':
        return (
          <ViewQuote
            createMode={true}
            editMode={true}
            onQuoteCreated={handleViewQuote}
            onBack={() => handlePageChange('quotes')}
            onSaveClick={true}
          />
        );
      case 'view-quote':
        return viewQuoteId ? (
          <ViewQuote
            quoteId={viewQuoteId}
            onBack={() => handlePageChange('quotes')}
            onConvertQuote={handleConvertQuote}
          />
        ) : (
          <div className="page-container">{t('app.quoteIdNotFound')}</div>
        );
      case 'edit-quote':
        return viewQuoteId ? (
          <ViewQuote
            quoteId={viewQuoteId}
            editMode={true}
            onBack={() => handlePageChange('quotes')}
          />
        ) : (
          <div className="page-container">{t('app.quoteIdNotFound')}</div>
        );
      case 'products-old':
        return (
          <ProductsList
            onViewProduct={handleViewProduct}
            onEditProduct={handleEditProduct}
            onNewProduct={handleNewProduct}
          />
        );
      case 'view-product':
        return viewProductId ? (
          <ViewProduct
            productId={viewProductId}
            onBack={() => handlePageChange('products')}
          />
        ) : (
          <div className="page-container">{t('app.productIdNotFound')}</div>
        );
      case 'edit-product':
        return viewProductId ? (
          <ViewProduct
            productId={viewProductId}
            editMode={true}
            onBack={() => handlePageChange('products')}
          />
        ) : (
          <div className="page-container">{t('app.productIdNotFound')}</div>
        );
      case 'new-product':
        return (
          <ViewProduct
            createMode={true}
            onBack={() => handlePageChange('products')}
            onProductCreated={(productId) => {
              handleViewProduct(productId);
            }}
          />
        );
      case 'inventory':
        return guard('inventory', <WarehouseHomePage
          initialDetailTarget={initialDetailTarget}
          onDetailConsumed={handleDetailConsumed}
        />);
      default:
        return <HomePage />;
    }
  };

  // Show loading screen while i18next is initializing
  if (!ready) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg-primary, #1a1a1a)',
        color: 'var(--text-primary, white)',
        fontFamily: 'monospace'
      }}>
        <div>{t('ui.loading', { defaultValue: 'Loading...' })}</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <UOMProvider>
          <TaskTypesProvider>
          <TabMemoryProvider>
            <NotificationProvider>
              <KeyboardNavigationProvider>
                <MessageProvider>
                  <ChatProvider>
                  <div className="app">
              <Header
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />

              <main className="app-main">
                <div className="terminal-output">
                  {renderPage()}
                </div>
              </main>

              <StatusBar />

              <MessageContainer />

              <footer className="app-footer">
                <div className="footer-left">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span className="status-dot" style={{ color: '#4CAF50' }}>●</span>
                    <span style={{ fontWeight: '600' }}>{t('app.footer.online')}</span>
                  </span>
                </div>
                <div className="footer-center">
                  <span className="footer-info">
                    <span className="info-arrow">▸</span> {t('app.footer.database', { name: 'MariaDB 11.0' })}
                  </span>
                  <span className="footer-info">
                    <span className="info-arrow">▸</span> Multipack
                  </span>
                  <span className="footer-info">
                    <span className="info-arrow">▸</span> {t('app.footer.sessionActive')}
                  </span>
                </div>
                <div className="footer-right">
                  <span>{t('app.footer.ready')}</span>
                </div>
              </footer>
                  </div>
                </ChatProvider>
                </MessageProvider>
              </KeyboardNavigationProvider>
            </NotificationProvider>
          </TabMemoryProvider>
          </TaskTypesProvider>
        </UOMProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/operator"
          element={
            <ProtectedRoute>
              <OperatorApp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainApp />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/home" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;